import React from "react";
import { Link } from "react-router-dom";
import {
  FaComments,
  FaCommentDots,
  FaHeart,
  FaPaperPlane,
  FaUserFriends,
  FaTrophy,
  FaSearch,
  FaArrowCircleRight,
  FaRegNewspaper,
} from "react-icons/fa";

export const INTERACOES_VAZIAS = {
  totalPosts: 0,
  totalComentarios: 0,
  totalCurtidas: 0,
  totalMensagensChat: 0,
  usuarioMaiorEngajamento: null,
  postagemMaisCurtida: null,
  postagemMaisComentada: null,
  usuarioMaisProcurado: null,
};

function fmt(n) {
  return Number(n ?? 0).toLocaleString("pt-BR");
}

function KpiCard({ label, value, hint, icon, tone = "blue" }) {
  return (
    <article className={`adb-kpi adb-kpi--tone-${tone} adb-kpi--compact`}>
      <div className="adb-kpi-body">
        <p className="adb-kpi-label">{label}</p>
        <div className="adb-kpi-metric">
          <p className="adb-kpi-value">{value}</p>
          <div className="adb-kpi-icon-watermark" aria-hidden>
            {icon}
          </div>
        </div>
        {hint ? <p className="adb-kpi-hint">{hint}</p> : null}
      </div>
    </article>
  );
}

function UsuarioDestaqueCard({
  titulo,
  usuario,
  metricaLabel,
  emptyMessage,
  icon,
  detalhe,
}) {
  if (!usuario) {
    return (
      <article className="apl-card adb-interacao-destaque">
        <div className="adb-interacao-destaque-head">
          <span className="adb-interacao-destaque-icon" aria-hidden>
            {icon}
          </span>
          <h3>{titulo}</h3>
        </div>
        <p className="adb-interacao-empty">{emptyMessage}</p>
      </article>
    );
  }

  return (
    <article className="apl-card adb-interacao-destaque">
      <div className="adb-interacao-destaque-head">
        <span className="adb-interacao-destaque-icon" aria-hidden>
          {icon}
        </span>
        <h3>{titulo}</h3>
      </div>
      <div className="adb-interacao-usuario">
        <div className="adb-interacao-usuario-info">
          <p className="adb-interacao-usuario-nome">{usuario.nome}</p>
          {usuario.empresa ? (
            <p className="adb-interacao-usuario-empresa">{usuario.empresa}</p>
          ) : null}
          {detalhe ? <p className="adb-interacao-usuario-detalhe">{detalhe}</p> : null}
        </div>
        <div className="adb-interacao-usuario-metric">
          <span className="adb-interacao-usuario-value">{fmt(usuario.quantidade)}</span>
          <span className="adb-interacao-usuario-metric-label">{metricaLabel}</span>
        </div>
      </div>
      <Link
        to={`/private/adm/convidados/editar/${usuario.id}`}
        className="adb-interacao-link"
      >
        <span>Ver cadastro</span>
        <FaArrowCircleRight size={14} aria-hidden />
      </Link>
    </article>
  );
}

function PostagemDestaqueCard({
  titulo,
  postagem,
  metricaLabel,
  emptyMessage,
  icon,
}) {
  if (!postagem) {
    return (
      <article className="apl-card adb-interacao-destaque">
        <div className="adb-interacao-destaque-head">
          <span className="adb-interacao-destaque-icon" aria-hidden>
            {icon}
          </span>
          <h3>{titulo}</h3>
        </div>
        <p className="adb-interacao-empty">{emptyMessage}</p>
      </article>
    );
  }

  const texto =
    postagem.titulo ||
    postagem.descricao ||
    `Postagem #${postagem.id}`;

  return (
    <article className="apl-card adb-interacao-destaque">
      <div className="adb-interacao-destaque-head">
        <span className="adb-interacao-destaque-icon" aria-hidden>
          {icon}
        </span>
        <h3>{titulo}</h3>
      </div>
      <p className="adb-interacao-post-texto">{texto}</p>
      <div className="adb-interacao-post-meta">
        <div>
          <p className="adb-interacao-usuario-nome">{postagem.autor?.nome}</p>
          {postagem.autor?.empresa ? (
            <p className="adb-interacao-usuario-empresa">
              {postagem.autor.empresa}
            </p>
          ) : null}
        </div>
        <div className="adb-interacao-usuario-metric">
          <span className="adb-interacao-usuario-value">
            {fmt(postagem.quantidade)}
          </span>
          <span className="adb-interacao-usuario-metric-label">
            {metricaLabel}
          </span>
        </div>
      </div>
      {postagem.autor?.id ? (
        <Link
          to={`/private/adm/convidados/editar/${postagem.autor.id}`}
          className="adb-interacao-link"
        >
          <span>Ver autor</span>
          <FaArrowCircleRight size={14} aria-hidden />
        </Link>
      ) : null}
    </article>
  );
}

function AdmDashInteracoes({ interacoes = INTERACOES_VAZIAS }) {
  const dados = { ...INTERACOES_VAZIAS, ...interacoes };
  const engajamento = dados.usuarioMaiorEngajamento;
  const detalheEngajamento = engajamento
    ? `${fmt(engajamento.posts)} posts · ${fmt(engajamento.comentarios)} comentários · ${fmt(engajamento.curtidas)} curtidas`
    : null;

  return (
    <section
      className="adb-section adb-tab-panel"
      id="adb-panel-interacoes"
      role="tabpanel"
      aria-labelledby="adb-tab-interacoes"
    >
      <h3 className="adb-section-title">Totais da timeline e chat</h3>
      <div className="adb-kpi-grid adb-kpi-grid--4 adb-kpi-grid--compact-row">
        <KpiCard
          label="Posts"
          value={fmt(dados.totalPosts)}
          hint="Postagens ativas na timeline"
          icon={<FaRegNewspaper size={18} />}
          tone="blue"
        />
        <KpiCard
          label="Comentários"
          value={fmt(dados.totalComentarios)}
          hint="Total de comentários na timeline"
          icon={<FaCommentDots size={18} />}
          tone="green"
        />
        <KpiCard
          label="Curtidas"
          value={fmt(dados.totalCurtidas)}
          hint="Total de curtidas na timeline"
          icon={<FaHeart size={18} />}
          tone="red"
        />
        <KpiCard
          label="Mensagens de chat"
          value={fmt(dados.totalMensagensChat)}
          hint="Mensagens trocadas no chat"
          icon={<FaPaperPlane size={18} />}
          tone="slate"
        />
      </div>

      <h3 className="adb-section-title">Destaques</h3>
      <div className="adb-interacao-destaques">
        <UsuarioDestaqueCard
          titulo="Maior engajamento na timeline"
          usuario={engajamento}
          metricaLabel="ações"
          emptyMessage="Ainda não há engajamento registrado na timeline."
          icon={<FaTrophy size={14} />}
          detalhe={detalheEngajamento}
        />
        <UsuarioDestaqueCard
          titulo="Usuário mais procurado"
          usuario={dados.usuarioMaisProcurado}
          metricaLabel="msgs recebidas"
          emptyMessage="Ainda não há mensagens de chat registradas."
          icon={<FaSearch size={14} />}
        />
        <PostagemDestaqueCard
          titulo="Postagem mais curtida"
          postagem={dados.postagemMaisCurtida}
          metricaLabel="curtidas"
          emptyMessage="Nenhuma postagem com curtidas ainda."
          icon={<FaHeart size={14} />}
        />
        <PostagemDestaqueCard
          titulo="Postagem mais comentada"
          postagem={dados.postagemMaisComentada}
          metricaLabel="comentários"
          emptyMessage="Nenhuma postagem com comentários ainda."
          icon={<FaComments size={14} />}
        />
      </div>

      <p className="adb-interacao-nota">
        <FaUserFriends size={12} aria-hidden />
        Engajamento na timeline = posts publicados + comentários feitos + curtidas
        dadas. Usuário mais procurado = quem mais recebeu mensagens no chat.
      </p>
    </section>
  );
}

export default AdmDashInteracoes;
